import { useEffect, useState } from "react";
import { fetchConeras, createConera, updateConera, deleteConera, Conera } from "../services/api";

export default function ConerasAdmin() {
  const [coneras, setConeras] = useState<Conera[]>([]);
  const [loading, setLoading] = useState(true);
  const [newId, setNewId] = useState("");
  const [newName, setNewName] = useState("");
  const [bulkIds, setBulkIds] = useState("");
  const [error, setError] = useState("");
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  function load() {
    setLoading(true);
    fetchConeras().then(setConeras).catch(() => setError("Error al cargar")).finally(() => setLoading(false));
  }
  useEffect(load, []);

  function toggleSelect(id: string) {
    setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  async function handleDeleteSelected() {
    if (selected.size === 0) return;
    if (!confirm(`Eliminar ${selected.size} coneras?`)) return;
    setError("");
    for (const id of selected) { try { await deleteConera(id); } catch (err) { setError(`Error ${id}`); } }
    setSelected(new Set()); load();
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault(); setError("");
    if (!newId.trim()) return;
    try { await createConera(newId.trim().toUpperCase(), newName.trim() || undefined); setNewId(""); setNewName(""); load(); }
    catch (err) { setError(err instanceof Error ? err.message : "Error"); }
  }

  async function handleBulkCreate(e: React.FormEvent) {
    e.preventDefault(); setError("");
    const ids = bulkIds.split(/[\n,]+/).map((s) => s.trim().toUpperCase()).filter(Boolean);
    if (ids.length === 0) { setError("Ingresa al menos un ID"); return; }
    let ok = 0, errs = "";
    for (const id of ids) { try { await createConera(id); ok++; } catch (err) { errs += (errs ? "; " : "") + `${id}: ${err instanceof Error ? err.message : "Error"}`; } }
    if (errs) setError(errs);
    if (ok > 0) { setBulkIds(""); load(); }
  }

  async function handleUpdate(id: string) {
    setError("");
    try { await updateConera(id, editName.trim()); setEditId(null); load(); }
    catch (err) { setError(err instanceof Error ? err.message : "Error"); }
  }

  async function handleDelete(id: string) {
    if (!confirm(`Eliminar conera ${id}?`)) return; setError("");
    try { await deleteConera(id); load(); } catch (err) { setError(err instanceof Error ? err.message : "Error"); }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-zinc-100 mb-6">Gestionar coneras</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <form onSubmit={handleCreate} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-3">
          <h3 className="font-semibold text-zinc-100 text-sm">Agregar una</h3>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">ID</label>
            <input type="text" value={newId} onChange={(e) => setNewId(e.target.value.toUpperCase())} placeholder="K143"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-600 transition" />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Nombre (opcional)</label>
            <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Conera K143"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-600 transition" />
          </div>
          <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">Agregar</button>
        </form>

        <form onSubmit={handleBulkCreate} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-3">
          <h3 className="font-semibold text-zinc-100 text-sm">Agregar varias</h3>
          <label className="block text-xs text-zinc-500">IDs (uno por linea o separados por coma)</label>
          <textarea value={bulkIds} onChange={(e) => setBulkIds(e.target.value.toUpperCase())} rows={4}
            placeholder={"K143\nK144\nK145"}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 font-mono focus:outline-none focus:ring-2 focus:ring-blue-600 transition resize-none" />
          <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">Agregar todas</button>
        </form>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-sm text-red-400 mb-4">{error}</div>}

      {loading ? (
        <div className="text-zinc-500 py-8 text-center">Cargando...</div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          {selected.size > 0 && (
            <div className="px-4 py-2 bg-zinc-800/50 border-b border-zinc-800 flex items-center gap-3">
              <span className="text-sm text-zinc-400">{selected.size} seleccionada(s)</span>
              <button onClick={handleDeleteSelected} className="text-xs text-red-400 hover:text-red-300 font-medium transition-colors">Eliminar seleccionadas</button>
            </div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="w-10 px-4 py-3">
                  <input type="checkbox" onChange={() => setSelected(selected.size === coneras.length ? new Set() : new Set(coneras.map((c) => c.id)))}
                    checked={selected.size === coneras.length && coneras.length > 0}
                    className="rounded border-zinc-600 bg-zinc-800" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase">ID</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Nombre</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {coneras.map((c) => (
                <tr key={c.id} className={`border-b border-zinc-800/50 ${selected.has(c.id) ? "bg-blue-500/5" : "hover:bg-zinc-800/50"}`}>
                  <td className="px-4 py-3 text-center">
                    <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)}
                      className="rounded border-zinc-600 bg-zinc-800" />
                  </td>
                  <td className="px-4 py-3 font-semibold text-zinc-100">{c.id}</td>
                  <td className="px-4 py-3 text-zinc-400">
                    {editId === c.id ? (
                      <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus
                        className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-600 w-full" />
                    ) : c.name}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {editId === c.id ? (
                      <div className="flex gap-1 justify-center">
                        <button onClick={() => handleUpdate(c.id)} className="text-xs bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 px-3 py-1 rounded-lg transition-colors">Guardar</button>
                        <button onClick={() => setEditId(null)} className="text-xs bg-zinc-800 text-zinc-400 hover:text-zinc-200 px-3 py-1 rounded-lg transition-colors">Cancelar</button>
                      </div>
                    ) : (
                      <div className="flex gap-1 justify-center">
                        <button onClick={() => { setEditId(c.id); setEditName(c.name); }} className="text-xs bg-zinc-800 text-zinc-400 hover:text-zinc-200 px-3 py-1 rounded-lg transition-colors">Editar</button>
                        <button onClick={() => handleDelete(c.id)} className="text-xs bg-red-500/10 text-red-400 hover:bg-red-500/20 px-3 py-1 rounded-lg transition-colors">Eliminar</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
